import {Component, computed, DestroyRef, inject, linkedSignal, signal, viewChild} from '@angular/core';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {DataService} from '../../../../services/store/data.service';
import {MaximumWorkingHours} from '../../../../classes/models/employee/MaximumWorkingHours';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {
  EmployeeMaximumWorkingHoursService
} from '../../../../services/http/employee/employee-maximum-working-hours.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Data interface for the MaxWorkingHoursForm modal.
 */
export interface MaxWorkingHoursFormData {
  /** Identifiers of an employee and position. */
  employeePositionId: EmployeePositionId;
  /** Employee contracted hours. */
  minHours: number;
  /** Previous maximum working hours. */
  previousMaximumWorkingHours?: MaximumWorkingHours;
  /** `True` if there are records of the selected week in backend. */
  weekExists?: boolean;
}

/**
 * This component handles creating or updating maximum working hours setting.
 */
@Component({
  selector: 'app-max-working-hours-form',
  templateUrl: './max-working-hours-form.component.html',
  styleUrls: ['./max-working-hours-form.component.less'],
  imports: [SHARED_IMPORTS]
})
export class MaxWorkingHoursFormComponent {

  /** A modal component of maximum working hours form. */
  readonly modalComponent = viewChild<ModalComponent>('modalComponent');
  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId>(undefined);
  /** Employee contracted hours. */
  readonly minHours = signal<number>(0);
  /** Previous maximum working hours. */
  readonly previousMaximumWorkingHours = signal<MaximumWorkingHours | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  readonly weekExists = signal<boolean>(false);
  /** `True` if a request to backend is in progress. */
  readonly loading = signal(false);
  /** A list of possible values of maximum working hours. */
  readonly hoursOptions = computed(() => {
    const options: number[] = [];
    for (let i = Math.ceil(this.minHours()); i <= 48; i++) {
      options.push(i);
    }
    return options;
  });
  /** A form value of maximum working hours of an employee. */
  readonly maxHours = linkedSignal(() => this.previousMaximumWorkingHours()?.hours);
  /** A form value of start date, from which maximum working hours should take effect. */
  readonly startDate = linkedSignal(() => this.previousMaximumWorkingHours()?.startDate);
  /** The first date from which maximum working hours are allowed to be edited. */
  readonly editDate: string;

  private readonly employeeMaximumWorkingHoursService = inject(EmployeeMaximumWorkingHoursService);
  private readonly toastr = inject(ToastrService);
  private readonly ruleCheckerService = inject(RuleCheckerService);
  private readonly dataService = inject(DataService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    /**
     * Sets up the first date from which maximum working hours are allowed to be edited.
     */
    this.editDate = this.dataService.editDate;
  }

  static open(modalService: NgbModal, data: MaxWorkingHoursFormData): NgbModalRef {
    const modalRef = modalService.open(MaxWorkingHoursFormComponent);
    const instance = modalRef.componentInstance as MaxWorkingHoursFormComponent;

    instance.employeePositionId.set(data.employeePositionId);
    instance.minHours.set(data.minHours);
    instance.previousMaximumWorkingHours.set(data.previousMaximumWorkingHours);
    instance.weekExists.set(data.weekExists ?? false);

    return modalRef;
  }

  /**
   * Sends request to backend to create or update maximum working hours setting.
   */
  onSubmit(): void {
    this.loading.set(true);
    const maximumWorkingHours = new MaximumWorkingHours(this.maxHours(), this.startDate());
    if (this.weekExists()) {
      this.editMaximumWorkingHours(maximumWorkingHours);
    } else {
      this.addMaximumWorkingHours(maximumWorkingHours);
    }
  }

  private addMaximumWorkingHours(maximumWorkingHours: MaximumWorkingHours): void {
    this.employeeMaximumWorkingHoursService
            .addMaximumWorkingHours(this.employeePositionId(), maximumWorkingHours)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Maximum working hours successfully created');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error?.message);
              }
            });
  }

  private editMaximumWorkingHours(maximumWorkingHours: MaximumWorkingHours): void {
    this.employeeMaximumWorkingHoursService
            .editMaximumWorkingHours(this.employeePositionId(), maximumWorkingHours)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.modalComponent()?.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Maximum working hours successfully updated');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading.set(false);
                this.toastr.error(error.error?.message);
              }
            });
  }
}